import { Avatar, Box, Button, Grid, Slider, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useForm, useWatch } from 'react-hook-form';

import { FILE_TYPES } from 'common/src/file-storage/constants';

import {
  changeProfilePicture,
  deleteProfilePicture,
} from 'portal/src/api/UsersService';
import useAuth from 'portal/src/auth/useAuth';
import { FormCard } from 'portal/src/components/common/forms/FormCard';
import FormController from 'portal/src/components/common/forms/FormController';
import { fileUploaderType } from 'portal/src/components/common/forms/FormFieldTypes';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

type ProfilePictureFormValues = {
  profilePicSrc: string;
  file: File | null;
};

const ProfilePictureDialog = ({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: {
    id: string;
    initials: string;
    values: ProfilePictureFormValues;
  };
}) => {
  const snackbarHandler = useSnackbarHandler();
  const { refreshUser, getUser } = useAuth();
  const { id } = getUser();

  const [slideValue, setSlideValue] = useState(1.0);

  const editorRef = useRef<AvatarEditor>(null);
  const acceptedTypes = `.${FILE_TYPES.profilePicture.join(', .')}`;

  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm({ values: user.values });
  const [watchedProfilePicSrc, watchedFile] = useWatch({
    name: ['profilePicSrc', 'file'],
    control,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: { file: File }) => {
      watchedFile
        ? await changeProfilePicture(user.id, payload)
        : await deleteProfilePicture(user.id);
      if (user.id === id) {
        await refreshUser();
      }
    },
    onSuccess: () => {
      closeAndResetDialog();
      snackbarHandler.success(
        `Profile picture ${watchedFile ? 'updated' : 'deleted'} successfully.`,
      );
    },
  });

  const closeAndResetDialog = () => {
    onClose();
    reset();
    setSlideValue(1.0);
  };

  const handleSave = useCallback(async (): Promise<void> => {
    let file;

    if (editorRef.current) {
      const avatarEditor: AvatarEditor = editorRef.current;

      try {
        const { name, type } = avatarEditor.props.image;
        const canvas = avatarEditor.getImageScaledToCanvas();

        if (canvas) {
          // Force update even if same image by providing a unique file name
          const timestamp = format(new Date(), 'T');
          const fileName = `${timestamp}-${name}`;

          const imagePromise = new Promise<File>((resolve, reject) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Canvas blob is null'));
                return;
              }

              const imageFile = new File([blob], fileName, { type });

              resolve(imageFile);
            }, type);
          });

          file = await imagePromise;
        }
      } catch (error) {
        console.error('Error processing image:', error);
        return;
      }
    }

    await mutate({ file });
  }, [mutate]);

  const handleSaveClick = useCallback(() => {
    handleSubmit(handleSave)();
  }, [handleSubmit, handleSave]);

  const handleFileChange = (e) => {
    setValue('profilePicSrc', e.name);
    setValue('file', e);
    setError('profilePicSrc', null);
    setSlideValue(1.0);
  };

  const handleDelete = () => {
    setValue('profilePicSrc', '', { shouldDirty: true });
    setValue('file', null, { shouldDirty: true });
    setError('profilePicSrc', null);
    setSlideValue(1.0);
  };

  const handleSliderChange = (_: Event, newValue: number) => {
    setSlideValue(newValue);
  };

  const fieldConf: fileUploaderType = {
    fileName: getValues('profilePicSrc'),
    multiple: false,
    required: false,
    type: 'fileUploader',
    name: 'profilePicSrc',
    label: 'Profile Picture',
    accept: acceptedTypes,
    validate: (file) => {
      if (!file) {
        return true;
      }

      const name = file instanceof File ? file.name : file;
      const extension = `.${name?.split('.').pop()?.toLowerCase()}`;
      if (acceptedTypes.includes(extension)) {
        return true;
      }
      return `Invalid extension file (accepted types: ${acceptedTypes}).`;
    },
    onChange: handleFileChange,
    onDelete: handleDelete,
  };

  return (
    <GenericDialog
      fullWidth={false}
      open={open}
      onClose={closeAndResetDialog}
      dialogActions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            disabled={isPending}
            onClick={closeAndResetDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            disabled={isPending || !isDirty}
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Stack>
      }
    >
      <FormCard title="Profile Picture Editor">
        <Box sx={{ justifyContent: 'center', display: 'grid' }}>
          {watchedFile && isDirty ? (
            <>
              <AvatarEditor
                ref={editorRef}
                image={watchedFile}
                width={250}
                height={250}
                borderRadius={150}
                scale={slideValue}
                rotate={0}
              />
              <Box sx={{ width: 300, mt: 2 }}>
                <Slider
                  size="medium"
                  value={slideValue}
                  onChange={handleSliderChange}
                  min={1.0}
                  max={3.0}
                  step={0.1}
                  marks={[
                    { value: 1.0, label: '1x' },
                    { value: 2.0, label: '2x' },
                    { value: 3.0, label: '3x' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </>
          ) : (
            <Avatar
              sx={{ width: 250, height: 250, fontSize: 100 }}
              src={watchedProfilePicSrc}
            >
              {user.initials}
            </Avatar>
          )}
        </Box>
        <Box>
          <Grid container spacing={2}>
            <Grid size={12} sx={{ width: 500 }}>
              <FormController control={control} fieldConf={fieldConf} />
            </Grid>
          </Grid>
        </Box>
      </FormCard>
    </GenericDialog>
  );
};

export default ProfilePictureDialog;
